import React from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';

interface GeneralTabProps {
  data: any;
  declaration: any;
  onChange: (data: any) => void;
}

const declarationTypes = [
  { value: 'import', label: 'Import' },
  { value: 'export', label: 'Export' },
  { value: 'transit', label: 'Transit' },
  { value: 're_export', label: 'Re-Export' },
  { value: 'temporary_import', label: 'Temporary Import' }
];

const currencies = [
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' }
];

const countries = [
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' }
];

const GeneralTab: React.FC<GeneralTabProps> = ({ data, declaration, onChange }) => {
  const { t, tString } = useTranslation();

  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  return (
      <Box>
        <SectionHeader
          title={t('customsDeclaration.general.title') as string}
          subtitle={t('customsDeclaration.general.subtitle') as string}
          icon={<InfoIcon color="primary" sx={{ fontSize: 32 }} />}
        />

        <Grid container spacing={3}>
          {/* Reference Numbers */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Reference Numbers
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Local Reference Number (LRN)"
              value={declaration?.reference_number || ''}
              InputProps={{
                readOnly: true
              }}
              helperText="Auto-generated reference number"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Movement Reference Number (MRN)"
              value={declaration?.mrn || ''}
              InputProps={{
                readOnly: true
              }}
              helperText="Assigned after submission to customs"
              placeholder="Will be assigned after submission"
            />
          </Grid>

          {/* Declaration Type & Country */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Declaration Details
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Declaration Type</InputLabel>
              <Select
                value={data.declaration_type || declaration?.declaration_type || 'export'}
                label="Declaration Type"
                onChange={(e) => handleChange('declaration_type', e.target.value)}
              >
                {declarationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Customs Office Country</InputLabel>
              <Select
                value={data.country_code || declaration?.country_code || 'DE'}
                label="Customs Office Country"
                onChange={(e) => handleChange('country_code', e.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Dates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Dates & Times
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Declaration Date"
              value={data.declaration_date ? data.declaration_date.substring(0, 16) : new Date().toISOString().substring(0, 16)}
              onChange={(e) => handleChange('declaration_date', new Date(e.target.value).toISOString())}
              InputLabelProps={{ shrink: true }}
              helperText="Date and time of declaration"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Acceptance Date"
              value={data.acceptance_date ? data.acceptance_date.substring(0, 16) : ''}
              onChange={(e) => handleChange('acceptance_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
              InputLabelProps={{ shrink: true }}
              helperText="Expected customs acceptance date"
            />
          </Grid>

          {/* Trade & Currency */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Trade Details
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Destination Country</InputLabel>
              <Select
                value={data.destination_country || ''}
                label="Destination Country"
                onChange={(e) => handleChange('destination_country', e.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Origin Country</InputLabel>
              <Select
                value={data.origin_country || ''}
                label="Origin Country"
                onChange={(e) => handleChange('origin_country', e.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Invoice Currency</InputLabel>
              <Select
                value={data.currency || 'EUR'}
                label="Invoice Currency"
                onChange={(e) => handleChange('currency', e.target.value)}
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency.value} value={currency.value}>
                    {currency.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Exchange Rate"
              type="number"
              value={data.exchange_rate || '1.0'}
              onChange={(e) => handleChange('exchange_rate', e.target.value)}
              inputProps={{ step: '0.0001', min: '0' }}
              helperText="Currency conversion rate to EUR"
            />
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Additional Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Information"
              value={data.additional_info || ''}
              onChange={(e) => handleChange('additional_info', e.target.value)}
              placeholder="Any additional notes or special instructions"
            />
          </Grid>
        </Grid>
      </Box>
  );
};

export default GeneralTab;
